'use strict';

/**
 * MarkupList — left-rail sidebar showing every markup on the PDF, grouped
 * by page. Click a row to select + jump to that page. Inline edit label/risk.
 * Trash to delete. + button at the top opens the "add markup" toolbar.
 */

import React, { useMemo, useState } from 'react';

const TYPE_ICON = { cloud: '☁︎', callout: '➜', scope_box: '▭' };
const TYPE_LABEL = { cloud: 'Cloud', callout: 'Callout', scope_box: 'Scope box' };
const RISK_DOT = {
  low: 'bg-teal-500',
  medium: 'bg-amber-500',
  high: 'bg-rose-600',
};

export default function MarkupList({
  markups = [],
  selectedIdx = -1,
  onSelectMarkup,
  onEditMarkup,
  onDeleteMarkup,
  onJumpToPage,
  onBeginAdd,
  addMode,
}) {
  const grouped = useMemo(() => {
    const map = new Map();
    markups.forEach((m, i) => {
      const k = m.page;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push({ idx: i, m });
    });
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [markups]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div className="text-sm font-semibold text-slate-700">
          Markups <span className="text-slate-500 font-normal">({markups.length})</span>
        </div>
        <AddMenu addMode={addMode} onBeginAdd={onBeginAdd} />
      </div>

      <div className="flex-1 overflow-y-auto">
        {grouped.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            No markups yet. Click <span className="font-mono">+</span> above and then click the PDF to drop one.
          </div>
        )}

        {grouped.map(([page, items]) => (
          <div key={page}>
            <button
              type="button"
              className="w-full px-3 py-1.5 text-left text-xs uppercase tracking-wide text-slate-500 hover:bg-slate-100 sticky top-0 bg-slate-50/95 backdrop-blur"
              onClick={() => onJumpToPage?.(page)}
            >
              Page {page} · {items.length}
            </button>
            {items.map(({ idx, m }) => (
              <MarkupRow
                key={idx}
                idx={idx}
                m={m}
                selected={selectedIdx === idx}
                onSelect={() => {
                  onSelectMarkup?.(idx);
                  onJumpToPage?.(m.page);
                }}
                onEdit={(patch) => onEditMarkup?.(idx, patch)}
                onDelete={() => {
                  if (window.confirm(`Delete markup #${idx + 1}?`)) onDeleteMarkup?.(idx);
                }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function AddMenu({ addMode, onBeginAdd }) {
  const [open, setOpen] = useState(false);
  if (addMode) {
    return (
      <button
        type="button"
        className="text-xs font-medium px-2 py-1 rounded bg-amber-100 text-amber-800 hover:bg-amber-200"
        onClick={() => onBeginAdd?.(null)}
      >
        Cancel ({TYPE_LABEL[addMode] || addMode})
      </button>
    );
  }
  return (
    <div className="relative">
      <button
        type="button"
        className="text-xs font-semibold px-2 py-1 rounded bg-slate-800 text-white hover:bg-slate-700"
        onClick={() => setOpen(!open)}
      >
        + Add
      </button>
      {open && (
        <div className="absolute right-0 top-7 z-20 bg-white rounded shadow-lg border border-slate-200 w-48 py-1 text-sm">
          {['cloud', 'callout', 'scope_box'].map((t) => (
            <button
              key={t}
              type="button"
              className="block w-full px-3 py-2 text-left hover:bg-slate-50"
              onClick={() => { setOpen(false); onBeginAdd?.(t); }}
            >
              <span className="mr-2 text-slate-400">{TYPE_ICON[t]}</span>
              {TYPE_LABEL[t]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MarkupRow({ idx, m, selected, onSelect, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.label || '');

  React.useEffect(() => { setDraft(m.label || ''); }, [m.label]);

  return (
    <div
      className={`group px-3 py-2 border-b border-slate-100 cursor-pointer ${selected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2">
        <div className={`w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center text-white ${RISK_DOT[m.risk] || RISK_DOT.medium}`}>
          {idx + 1}
        </div>
        <div className="text-xs text-slate-500 flex-1">
          <span className="font-mono mr-2">{TYPE_ICON[m.type]}</span>
          {TYPE_LABEL[m.type]}
        </div>
        <select
          value={m.risk}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onEdit?.({ risk: e.target.value })}
          className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white"
        >
          <option value="low">low</option>
          <option value="medium">medium</option>
          <option value="high">high</option>
        </select>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
          className="opacity-0 group-hover:opacity-100 text-rose-600 hover:text-rose-700 text-xs px-1"
          title="Delete markup"
        >
          🗑
        </button>
      </div>

      {!editing ? (
        <div
          className="mt-1 text-[13px] text-slate-700 leading-snug line-clamp-3 group/label flex items-start gap-1 hover:bg-slate-50 rounded px-1 -mx-1 py-0.5 cursor-text"
          onClick={(e) => { e.stopPropagation(); onSelect?.(); setEditing(true); }}
          title="Click to edit label"
        >
          <span className="flex-1">{m.label || <span className="italic text-slate-400">no label — click to add</span>}</span>
          <span className="text-slate-300 group-hover/label:text-slate-500 text-[11px] mt-0.5">✎</span>
        </div>
      ) : (
        <div className="mt-1" onClick={(e) => e.stopPropagation()}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full text-[13px] border border-slate-300 rounded px-2 py-1 resize-y min-h-[60px]"
            autoFocus
          />
          <div className="flex gap-2 mt-1 text-xs">
            <button
              type="button"
              onClick={() => { onEdit?.({ label: draft }); setEditing(false); }}
              className="px-2 py-1 rounded bg-slate-800 text-white hover:bg-slate-700"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => { setDraft(m.label || ''); setEditing(false); }}
              className="px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
